import React, { useRef } from 'react';
import { motion, useMotionValue, useSpring } from 'motion/react';
import { Trophy } from 'lucide-react';
import { cn } from '../lib/utils';
import { Card, Suit } from '../types';
import { SUIT_IMG_MAP } from '../lib/constants';
import { useSettings } from './SettingsContext';

export interface CardUIProps {
  card: Card; 
  onClick?: () => void; 
  isHidden?: boolean; 
  isSelected?: boolean; 
  disabled?: boolean;
  isRevealing?: boolean;
  scale?: number;
  className?: string;
}

const CardSuitIcon = ({ suit, className }: { suit: Suit, className?: string }) => {
  switch(suit) {
    case Suit.COINS: // Denari
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={cn("suit-denari", className)}>
          <circle cx="12" cy="12" r="8" />
          <circle cx="12" cy="12" r="3" fill="currentColor" />
        </svg>
      );
    case Suit.CUPS: // Coppe
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" className={cn("suit-coppe", className)}>
          <path d="M7 3h10v5c0 2.8-2.2 5-5 5s-5-2.2-5-5V3z" />
          <path d="M12 13v7" />
          <path d="M8 20h8" />
        </svg>
      );
    case Suit.SWORDS: // Spade
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" className={cn("suit-spade", className)}>
          <path d="M12 2v15" />
          <path d="M9 17h6" />
          <path d="M12 17v4" />
          <path d="M8 6h8" />
        </svg>
      );
    case Suit.CLUBS: // Bastoni
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" className={cn("suit-bastoni", className)}>
          <path d="M12 3v18" />
          <path d="M9 7h6" />
          <path d="M9 13h6" />
          <path d="M9 19h6" />
        </svg>
      );
  }
};

const SPRITE_URL = '/sprites/sprite_small.webp';

const getCardStyle = (suit: Suit, value: number) => {
  return {};
};

type IdleCapableWindow = Window & {
  requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
};

let representativePreloadScheduled = false;
let representativePreloadComplete = false;

function preloadRepresentativeCards(): void {
  if (representativePreloadComplete || typeof window === 'undefined') return;
  representativePreloadComplete = true;

  const sources = [
    '/cards_optimized/denari_1.webp',
    '/cards_optimized/coppe_1.webp',
    '/cards_optimized/spade_1.webp',
    '/cards_optimized/bastoni_1.webp',
    '/cards_optimized/card_back.png',
  ];

  for (const src of sources) {
    const image = new window.Image();
    image.decoding = 'async';
    image.src = src;
  }
}

/**
 * The historical name is kept for callers, but this never preloads the full
 * 40-card deck. The first call schedules five representative assets for idle
 * time; a second call (normally explicit play intent) starts them immediately.
 */
export const preloadAllCards = () => {
  if (representativePreloadComplete || typeof window === 'undefined') return;

  if (representativePreloadScheduled) {
    preloadRepresentativeCards();
    return;
  }

  const idleWindow = window as IdleCapableWindow;
  if (typeof idleWindow.requestIdleCallback === 'function') {
    representativePreloadScheduled = true;
    idleWindow.requestIdleCallback(preloadRepresentativeCards, { timeout: 2500 });
    return;
  }

  preloadRepresentativeCards();
};

const CardIMG = ({ suit, value, className, ...props }: { suit: Suit; value: number } & React.ImgHTMLAttributes<HTMLImageElement>) => {
  const suitName = SUIT_IMG_MAP[suit];
  return <img src={`/cards_optimized/${suitName}_${value}.webp`} width="344" height="593" loading="lazy" decoding="async" className={cn("object-fill", className)} {...props} />;
};

export const CardUI: React.FC<CardUIProps> = React.memo(({ card, onClick, isHidden, isSelected, disabled, isRevealing, scale = 1, className }) => {
  const { cardBackTheme } = useSettings();
  
  const cardRef = useRef<HTMLDivElement>(null);

  // Motion values for tilt animation
  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);

  // Springs for smooth movement
  const springX = useSpring(rotateX, { stiffness: 200, damping: 20 });
  const springY = useSpring(rotateY, { stiffness: 200, damping: 20 });

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled || isHidden) return;
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5; // -0.5 to 0.5
    const y = (e.clientY - rect.top) / rect.height - 0.5; // -0.5 to 0.5
    rotateX.set(-y * 20); // Rotate up to 20deg on X
    rotateY.set(x * 20);  // Rotate up to 20deg on Y
  };

  const handleMouseLeave = () => {
    rotateX.set(0);
    rotateY.set(0);
  };

  if (isHidden) {
    let bgClasses = "bg-stone-800 border-stone-900";
    let iconClass = "text-stone-700";
    let innerBorderClass = "border-white/5";
    let overlayClass = "opacity-10 bg-[radial-gradient(circle_at_center,_white_1px,_transparent_1px)]";

    if (cardBackTheme === 'gold') {
      bgClasses = "bg-gradient-to-br from-amber-700 via-yellow-900 to-amber-950 border-yellow-500/60";
      iconClass = "text-yellow-400 drop-shadow-[0_0_8px_rgba(250,204,21,0.5)]";
      innerBorderClass = "border-yellow-400/30";
      overlayClass = "opacity-20 bg-[radial-gradient(circle_at_center,_#facc15_1px,_transparent_1px)]";
    } else if (cardBackTheme === 'modern') {
      bgClasses = "bg-gradient-to-br from-zinc-900 via-slate-800 to-zinc-950 border-cyan-500/60";
      iconClass = "text-cyan-400 drop-shadow-[0_0_8px_rgba(34,211,238,0.5)]";
      innerBorderClass = "border-cyan-400/30";
      overlayClass = "opacity-15 bg-[radial-gradient(circle_at_center,_#22d3ee_1px,_transparent_1px)]";
    }

    return (
      <motion.div
        animate={isRevealing ? { rotateY: [0, 90, 0], scale: [0.8, 1.1, 1] } : {}}
        transition={isRevealing ? { duration: 0.5, ease: "easeInOut" } : {}}
        style={{ perspective: '600px' }}
      >
        <div
          className="relative overflow-hidden rounded-lg border border-white/10 shadow-2xl"
          style={{ width: `${48 * scale}px`, aspectRatio: '800/1323' }}
        >
          <div className={cn("absolute inset-0 rounded-lg border-2 flex items-center justify-center p-1", bgClasses)}>
            <div className={cn("absolute inset-0 bg-[length:8px_8px]", overlayClass)} />
            <div className={cn("w-full h-full border rounded-md flex items-center justify-center", innerBorderClass)}>
              <Trophy className={iconClass} style={{ width: `${24 * scale}px`, height: `${24 * scale}px` }} />
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      id={card.id}
      layout
      ref={cardRef}
      initial={isRevealing ? { scale: 0.3, opacity: 0, rotate: -15 } : { scale: 1, opacity: 1, rotate: 0 }}
      animate={{ scale: 1, opacity: 1, rotate: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      whileHover={!disabled ? { scale: 1.05 } : {}}
      whileTap={!disabled ? { scale: 0.95 } : {}}
      onClick={!disabled ? onClick : undefined}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={cn(
        "relative cursor-pointer",
        disabled ? "cursor-default" : "",
        className
      )}
      style={{ willChange: "transform", perspective: '1000px' }}
    >
      <motion.div 
        className="relative rounded-lg overflow-hidden border border-white/20 shadow-md" 
        style={{ 
          width: `${64 * scale}px`, 
          aspectRatio: '800/1323',
          rotateX: springX,
          rotateY: springY,
          transformStyle: 'preserve-3d'
        }}
      >
        <CardIMG
          suit={card.suit}
          value={card.value}
          className="absolute inset-0 w-full h-full rounded-lg overflow-hidden pointer-events-none object-fill"
          style={{ willChange: "contents", backfaceVisibility: "hidden" }}
        />

        {/* Fix #4: Indicators scale with parent transform via CSS transform-origin */}
        <motion.div
          className="absolute top-1 right-1 flex flex-col items-end gap-1 pointer-events-none z-20"
          animate={isSelected ? { scale: 1.2 } : { scale: 1 }}
          style={{ transformOrigin: 'top right', transform: 'translateZ(10px)' }}
        />
      </motion.div>
    </motion.div>
  );
});
